/**
 * StudyGroupDoc + SGDeckDoc — per-group Yjs document, scoped per-deck via views.
 *
 * Exact structural clone of `UserNotesDoc`/`DeckDoc` (see deck-doc.ts). One
 * root Y.Doc per group hosts ALL collaborative content for every shared
 * deck in that group: notes today, annotations/handwriting/embeds later.
 *
 *   Y.Doc("sg:{groupId}")
 *     ├─ __meta: Y.Map<string, unknown>       // schemaVersion, seededFromLegacy
 *     └─ decks:  Y.Map<deckId, Y.Map>         // one sub-map per shared deck
 *
 *   Deck sub-map (keyed under `decks[deckId]`):
 *     ├─ notes:        Y.Map<noteId, NoteItem>
 *     ├─ annotations:  Y.Map<...>                    // reserved
 *     ├─ handwriting:  Y.Map<pageNum, Y.Array<...>>  // reserved
 *     └─ embeds:       Y.Map<id, Y.Map<...>>         // reserved
 *
 * The `NoteItem` shape is identical to UserNotesDoc — we re-use the same
 * NOTE_ITEM_KEYS from deck-doc.ts so tiptap / youtube / link notes work
 * across personal and shared decks without duplication.
 *
 * This file owns NO lifecycle. The Y.Doc + provider are managed by the
 * `<StudyGroupYDocProvider>` (see hooks-study-group.tsx). Instances are
 * cheap; construct a StudyGroupDoc per hook invocation.
 */

import * as Y from 'yjs';

import {
  DECK_SUBMAP_KEYS,
  DEFAULT_NOTE_ID,
  type LinkNoteMeta,
  NOTE_ITEM_KEYS,
  type NoteKind,
  type YoutubeNoteMeta,
} from './deck-doc';

// ---------------------------------------------------------------------------
// Diagnostic: pre-sync auto-create detector (study-group scope)
// ---------------------------------------------------------------------------
// Mirror of `warnIfPreSyncAutoCreate` in deck-doc.ts. SG groups typically
// have many member clientIDs in their store, so a pre-sync doc is even
// easier to spot — clientCount of ≤1 means we haven't received the
// server's sync-step2 yet.

function warnIfPreSyncSGAutoCreate(ydoc: Y.Doc, deckName: string, noteId: string): void {
  try {
    const clientCount = ydoc.store.clients.size;
    const decksMap = ydoc.getMap<unknown>('decks');
    const deckCount = decksMap.size;
    if (clientCount <= 1 && deckCount === 0) {
      const stateBytes = Y.encodeStateAsUpdate(ydoc).byteLength;

      console.warn(
        '[Yjs SG auto-create on empty doc] possible data-loss race — ' +
          'SG getNoteFragment fired before the group doc was synced.',
        {
          deckName,
          noteId,
          clientCount,
          deckCount,
          stateBytes,
          localClientID: ydoc.clientID,
        },
      );
    }
  } catch {
    // Diagnostic must never throw.
  }
}

// ---------------------------------------------------------------------------
// Schema constants (study-group scope)
// ---------------------------------------------------------------------------

/** Top-level keys on the group Y.Doc — same shape as UserNotesDoc. */
export const STUDY_GROUP_DOC_KEYS = {
  decks: 'decks',
  meta: '__meta',
} as const;

/** Current schema version for the group Y.Doc; bump on non-additive changes. */
export const STUDY_GROUP_DOC_SCHEMA_VERSION = 1;

// Re-export for callers that want a single import.
export { DECK_SUBMAP_KEYS, DEFAULT_NOTE_ID, NOTE_ITEM_KEYS };
export type { LinkNoteMeta, NoteKind, YoutubeNoteMeta };

// ---------------------------------------------------------------------------
// StudyGroupDoc — root accessor over the group Y.Doc
// ---------------------------------------------------------------------------

/**
 * Wrapper around the per-group Y.Doc. Stamps the schema version on first
 * touch (idempotent). Cheap to construct.
 */
export class StudyGroupDoc {
  readonly ydoc: Y.Doc;
  readonly groupId: string;

  constructor(groupId: string, ydoc: Y.Doc) {
    this.ydoc = ydoc;
    this.groupId = groupId;
    const meta = this.ydoc.getMap<unknown>(STUDY_GROUP_DOC_KEYS.meta);
    if (meta.get('schemaVersion') !== STUDY_GROUP_DOC_SCHEMA_VERSION) {
      meta.set('schemaVersion', STUDY_GROUP_DOC_SCHEMA_VERSION);
    }
  }

  /** Access the `__meta` Y.Map. */
  meta(): Y.Map<unknown> {
    return this.ydoc.getMap<unknown>(STUDY_GROUP_DOC_KEYS.meta);
  }

  /** Has the one-shot Supabase → Yjs seed run for this group? */
  get seededFromLegacy(): boolean {
    return this.meta().get('seededFromLegacy') === true;
  }

  /** Mark the legacy seed as done. Idempotent. */
  markSeededFromLegacy(): void {
    this.meta().set('seededFromLegacy', true);
  }

  /** The `decks` Y.Map — one entry per shared deck. */
  decksMap(): Y.Map<Y.Map<unknown>> {
    return this.ydoc.getMap<Y.Map<unknown>>(STUDY_GROUP_DOC_KEYS.decks);
  }

  /** Get (or create) the sub-map for a deck. */
  getOrCreateDeckSubmap(deckId: string): Y.Map<unknown> {
    const decks = this.decksMap();
    let sub = decks.get(deckId);
    if (!sub) {
      sub = new Y.Map<unknown>();
      decks.set(deckId, sub);
    }
    return sub;
  }

  /** Get the sub-map for a deck, or `undefined` if not yet present. */
  getDeckSubmap(deckId: string): Y.Map<unknown> | undefined {
    return this.decksMap().get(deckId);
  }

  /** Delete a deck's entire sub-map. Called on deck-remove. */
  deleteDeckSubmap(deckId: string): void {
    const decks = this.decksMap();
    if (decks.has(deckId)) {
      decks.delete(deckId);
    }
  }

  /** Construct a per-deck view. Cheap. */
  deck(deckId: string): SGDeckDoc {
    return new SGDeckDoc(deckId, this);
  }
}

// ---------------------------------------------------------------------------
// SGDeckDoc — per-deck view over a slice of the group doc
// ---------------------------------------------------------------------------

/**
 * Per-deck API over the group's Y.Doc. Mirrors `DeckDoc` in deck-doc.ts
 * one-for-one so consumer code (tiptap mount, seedNotesFromLegacyIfEmpty,
 * etc.) compiles unchanged regardless of whether the deck is personal or
 * group-shared.
 *
 * The wrapper owns no Yjs state — it dereferences into
 * `groupDoc.decksMap().get(deckId)` on every call.
 */
export class SGDeckDoc {
  readonly deckName: string;
  readonly groupDoc: StudyGroupDoc;

  constructor(deckName: string, groupDoc: StudyGroupDoc) {
    this.deckName = deckName;
    this.groupDoc = groupDoc;
  }

  /**
   * The group-level Y.Doc. Exposed so callers can batch writes inside a
   * single `ydoc.transact()` — the transaction is at the group doc level,
   * which is correct (atomic apply avoids intermediate sync messages).
   */
  get ydoc(): Y.Doc {
    return this.groupDoc.ydoc;
  }

  /** The deck sub-map, creating it if missing. */
  private submap(): Y.Map<unknown> {
    return this.groupDoc.getOrCreateDeckSubmap(this.deckName);
  }

  /** The deck sub-map without creating — `undefined` if not yet present. */
  private submapOrUndefined(): Y.Map<unknown> | undefined {
    return this.groupDoc.getDeckSubmap(this.deckName);
  }

  // ── Notes (multi-note, typed — same kinds as personal decks) ────────

  /** The `notes` Y.Map under this deck, creating it if missing. */
  private notesMap(): Y.Map<Y.Map<unknown>> {
    const sub = this.submap();
    let notes = sub.get(DECK_SUBMAP_KEYS.notes) as Y.Map<Y.Map<unknown>> | undefined;
    if (!notes) {
      notes = new Y.Map<Y.Map<unknown>>();
      sub.set(DECK_SUBMAP_KEYS.notes, notes);
    }
    return notes;
  }

  /** The `notes` Y.Map or `undefined` if missing (read-only callers). */
  private notesMapOrUndefined(): Y.Map<Y.Map<unknown>> | undefined {
    const sub = this.submapOrUndefined();
    return sub?.get(DECK_SUBMAP_KEYS.notes) as Y.Map<Y.Map<unknown>> | undefined;
  }

  /** All note ids currently present on this deck. */
  listNoteIds(): string[] {
    const notes = this.notesMapOrUndefined();
    return notes ? Array.from(notes.keys()) : [];
  }

  /** Kind of a given note, or `undefined` if the note doesn't exist. */
  getNoteKind(noteId: string): NoteKind | undefined {
    const notes = this.notesMapOrUndefined();
    const item = notes?.get(noteId);
    const type = item?.get(NOTE_ITEM_KEYS.type);
    if (type === 'tiptap' || type === 'youtube' || type === 'link') {
      return type;
    }
    return undefined;
  }

  /**
   * Ensure a NoteItem Y.Map exists for `noteId` with the given kind.
   * Does NOT convert existing items — conversions need an explicit caller
   * that knows old/new fields to preserve.
   */
  private ensureNoteItem(noteId: string, kind: NoteKind): Y.Map<unknown> {
    const notes = this.notesMap();
    let item = notes.get(noteId);
    if (!item) {
      item = new Y.Map<unknown>();
      notes.set(noteId, item);
    }
    if (item.get(NOTE_ITEM_KEYS.type) == null) {
      item.set(NOTE_ITEM_KEYS.type, kind);
    }
    return item;
  }

  // ── Tiptap notes ───────────────────────────────────────────────────

  /**
   * Get (or create) the Y.XmlFragment backing a tiptap note.
   * Returns `null` if a note of a different kind exists at this id.
   */
  getNoteFragment(noteId: string = DEFAULT_NOTE_ID): Y.XmlFragment | null {
    const existingKind = this.getNoteKind(noteId);
    if (existingKind !== undefined && existingKind !== 'tiptap') return null;
    if (existingKind === undefined) {
      warnIfPreSyncSGAutoCreate(this.groupDoc.ydoc, this.deckName, noteId);
    }
    const item = this.ensureNoteItem(noteId, 'tiptap');
    let frag = item.get(NOTE_ITEM_KEYS.content) as Y.XmlFragment | undefined;
    if (!frag) {
      frag = new Y.XmlFragment();
      item.set(NOTE_ITEM_KEYS.content, frag);
    }
    return frag;
  }

  /** Side-effect-free variant for read-only observers. */
  peekNoteFragment(noteId: string = DEFAULT_NOTE_ID): Y.XmlFragment | null {
    if (this.getNoteKind(noteId) !== 'tiptap') return null;
    const item = this.notesMapOrUndefined()?.get(noteId);
    const frag = item?.get(NOTE_ITEM_KEYS.content) as Y.XmlFragment | undefined;
    return frag ?? null;
  }

  /**
   * True if no note on this deck has meaningful content.
   * Same semantics as DeckDoc.hasNoNotes on personal decks.
   */
  hasNoNotes(): boolean {
    const notes = this.notesMapOrUndefined();
    if (!notes || notes.size === 0) return true;
    for (const item of notes.values()) {
      const kind = item.get(NOTE_ITEM_KEYS.type);
      if (kind === 'tiptap') {
        const frag = item.get(NOTE_ITEM_KEYS.content) as Y.XmlFragment | undefined;
        if (frag && frag.length > 0) return false;
      } else if (kind === 'youtube' || kind === 'link') {
        if (item.get(NOTE_ITEM_KEYS.url)) return false;
      }
    }
    return true;
  }

  // ── YouTube notes ──────────────────────────────────────────────────

  getYoutubeNoteMeta(noteId: string): YoutubeNoteMeta | undefined {
    if (this.getNoteKind(noteId) !== 'youtube') return undefined;
    const item = this.notesMapOrUndefined()?.get(noteId);
    if (!item) return undefined;
    const url = item.get(NOTE_ITEM_KEYS.url) as string | undefined;
    if (!url) return undefined;
    return {
      url,
      title: item.get(NOTE_ITEM_KEYS.title) as string | undefined,
      thumbnail: item.get(NOTE_ITEM_KEYS.thumbnail) as string | undefined,
      description: item.get(NOTE_ITEM_KEYS.description) as string | undefined,
    };
  }

  setYoutubeNoteMeta(noteId: string, meta: YoutubeNoteMeta): void {
    this.ydoc.transact(() => {
      const item = this.ensureNoteItem(noteId, 'youtube');
      item.set(NOTE_ITEM_KEYS.type, 'youtube');
      item.set(NOTE_ITEM_KEYS.url, meta.url);
      if (meta.title != null) item.set(NOTE_ITEM_KEYS.title, meta.title);
      if (meta.thumbnail != null) item.set(NOTE_ITEM_KEYS.thumbnail, meta.thumbnail);
      if (meta.description != null)
        item.set(NOTE_ITEM_KEYS.description, meta.description);
    });
  }

  // ── Link notes ─────────────────────────────────────────────────────

  getLinkNoteMeta(noteId: string): LinkNoteMeta | undefined {
    if (this.getNoteKind(noteId) !== 'link') return undefined;
    const item = this.notesMapOrUndefined()?.get(noteId);
    if (!item) return undefined;
    const url = item.get(NOTE_ITEM_KEYS.url) as string | undefined;
    if (!url) return undefined;
    return {
      url,
      title: item.get(NOTE_ITEM_KEYS.title) as string | undefined,
      description: item.get(NOTE_ITEM_KEYS.description) as string | undefined,
      favicon: item.get(NOTE_ITEM_KEYS.favicon) as string | undefined,
    };
  }

  setLinkNoteMeta(noteId: string, meta: LinkNoteMeta): void {
    this.ydoc.transact(() => {
      const item = this.ensureNoteItem(noteId, 'link');
      item.set(NOTE_ITEM_KEYS.type, 'link');
      item.set(NOTE_ITEM_KEYS.url, meta.url);
      if (meta.title != null) item.set(NOTE_ITEM_KEYS.title, meta.title);
      if (meta.description != null)
        item.set(NOTE_ITEM_KEYS.description, meta.description);
      if (meta.favicon != null) item.set(NOTE_ITEM_KEYS.favicon, meta.favicon);
    });
  }

  /** Delete a note outright. Works for any kind. */
  deleteNote(noteId: string): void {
    const notes = this.notesMapOrUndefined();
    if (notes?.has(noteId)) notes.delete(noteId);
  }

  // ── Reserved sub-slices (parity with DeckDoc) ──────────────────────

  getAnnotationsMap(): Y.Map<unknown> {
    const sub = this.submap();
    let m = sub.get(DECK_SUBMAP_KEYS.annotations) as Y.Map<unknown> | undefined;
    if (!m) {
      m = new Y.Map();
      sub.set(DECK_SUBMAP_KEYS.annotations, m);
    }
    return m;
  }

  getHandwritingArray(page: number): Y.Array<unknown> {
    const sub = this.submap();
    let hw = sub.get(DECK_SUBMAP_KEYS.handwriting) as Y.Map<Y.Array<unknown>> | undefined;
    if (!hw) {
      hw = new Y.Map<Y.Array<unknown>>();
      sub.set(DECK_SUBMAP_KEYS.handwriting, hw);
    }
    const key = `page-${page}`;
    let arr = hw.get(key);
    if (!arr) {
      arr = new Y.Array();
      hw.set(key, arr);
    }
    return arr;
  }

  getEmbed(id: string): Y.Map<unknown> {
    const sub = this.submap();
    let embeds = sub.get(DECK_SUBMAP_KEYS.embeds) as Y.Map<Y.Map<unknown>> | undefined;
    if (!embeds) {
      embeds = new Y.Map<Y.Map<unknown>>();
      sub.set(DECK_SUBMAP_KEYS.embeds, embeds);
    }
    let m = embeds.get(id);
    if (!m) {
      m = new Y.Map();
      embeds.set(id, m);
    }
    return m;
  }
}
